import {
  Box,
  Button,
  Center,
  Flex,
  Spinner,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Text,
  useDisclosure,
} from "@chakra-ui/react";

import { observer } from "mobx-react";
import { useStores } from "stores/setup/use-store";

import { BackButton } from "components/BackButton";
import { useTranslation } from "react-i18next";
import { SCREEN_HORIZONTAL_SPACING } from "../../constants";

import { StoreState } from "enum/StoreState";
import { ForecastTableLoading } from "./components/ForecastTableLoading";
import { ManageTabularForecastColumnsModal } from "./components/ManageTabularForecastColumnsModal";
import { SelectStation } from "./components/SelectStation";
import { TableHeader } from "./components/TableHeader";
import { TableRow } from "./components/TableRow";
import { useForecastTableData } from "./hooks/useForecastTableData";
import { useHidedColumns } from "./hooks/useHidedCollumns";
import { useSelectedStation } from "./hooks/useSelectedStation";
import { useThreshold } from "./hooks/useThreshold";
import { useUserHasScrolled } from "./hooks/useUserHasScrolled";

function ForecastTable() {
  const {
    dataStores: { forecastChartPageStore },
  } = useStores();

  const { isOpen, onOpen, onClose } = useDisclosure();

  const {
    thresholds,
    handleThresholdsInputChange,
    handleThresholdOperatorToggle,
  } = useThreshold();

  const {
    hidedColumns,
    hidedSubColumns,
    onUpdateHidedColumns,
    onHideSubCollumn,
    isColumnHided,
    isSubColumnHided,
    onHideEnviromentCollumn,
    onUpdateSubHidedColumns,
  } = useHidedColumns();

  const { selectedStation, onChangeSelectedStation } = useSelectedStation();

  const { columns, getColorForValue, getType, rows } =
    useForecastTableData(selectedStation);

  const { hasScrolled } = useUserHasScrolled("table-forecast-table");

  const { t } = useTranslation("translation", {
    keyPrefix: "forecastChart.forecastTable",
  });
  const { t: environmentTranslation } = useTranslation("translation", {
    keyPrefix: "forecastChart.forecastTable.environmentalVariableEnum",
  });

  const numberOfVisibileColumns = columns.length - hidedColumns.length === 2;

  return (
    <Box mt={4} mb={4} mx={SCREEN_HORIZONTAL_SPACING} overflowX="hidden">
      <Stack>
        <Flex justifyContent="space-between" flex={1} pb={4}>
          <Center gap={2}>
            <BackButton backRoute="/forecast-charts" />
            <Text color={"newBlue.500"}>{t("title")}</Text>
          </Center>
          <Flex align="center" justify="center" gap={2}>
            <SelectStation
              translate={t}
              stations={forecastChartPageStore.stations}
              selectedStation={selectedStation}
              onChange={onChangeSelectedStation}
              state={forecastChartPageStore.state}
            />

            {(forecastChartPageStore.state === StoreState.PEDDING ||
              forecastChartPageStore.forecastReportState ===
                StoreState.PEDDING) && (
              <Spinner
                thickness="4px"
                color="newBlue.500"
                size="sm"
                zIndex={99999999999}
              />
            )}

            {forecastChartPageStore.state === StoreState.DONE &&
              forecastChartPageStore.forecastReportState ===
                StoreState.DONE && (
                <Button
                  color="white"
                  layerStyle="ocean"
                  _hover={{ layerStyle: "ocean" }}
                  onClick={onOpen}
                >
                  {t("manageColumns")}
                </Button>
              )}
          </Flex>
        </Flex>

        {forecastChartPageStore.forecastReportState === StoreState.PEDDING && (
          <ForecastTableLoading translate={t} />
        )}

        {forecastChartPageStore.forecastReportState === StoreState.ERROR && (
          <Center>
            <Text color="black">{t("getDataError")}</Text>
          </Center>
        )}

        {forecastChartPageStore.forecastReportState === StoreState.DONE && (
          <TableContainer
            maxHeight="88vh"
            height="89vh"
            maxWidth="100%"
            overflowX="scroll"
            overflowY="scroll"
            id="table-forecast-table"
            sx={{
              table: {
                borderCollapse: "separate",
                borderSpacing: 0,
              },
            }}
          >
            <Table variant="unstyled" size="sm">
              <TableHeader
                columns={columns}
                tresholds={thresholds}
                verifyIfColumnIsHided={isColumnHided}
                environmentTranslation={environmentTranslation}
                translate={t}
                isHideColumnDisabled={numberOfVisibileColumns}
                onHideColumn={onHideEnviromentCollumn}
                onOpenManageColumnsModal={onOpen}
                onToggleOperator={handleThresholdOperatorToggle}
                onTresholdInputChange={handleThresholdsInputChange}
                isSubColumnHided={isSubColumnHided}
                onHideSubCollumn={onHideSubCollumn}
                hidedColumns={hidedColumns}
                hidedSubColumns={hidedSubColumns}
              />
              <Tbody>
                {rows.map((row, rowIndex) => {
                  const rowId = row[0]?.id || `row-${rowIndex}`;
                  return (
                    <TableRow
                      key={rowId}
                      row={row}
                      index={rowIndex}
                      thresholds={thresholds}
                      getColorForValue={getColorForValue}
                      verifyIfColumnHided={isColumnHided}
                      isSubColumnHided={isSubColumnHided}
                      verifyColumnType={getType}
                      hasScrolled={hasScrolled}
                    />
                  );
                })}
              </Tbody>
            </Table>
          </TableContainer>
        )}
      </Stack>

      <ManageTabularForecastColumnsModal
        isOpen={isOpen}
        onClose={onClose}
        hidedColumns={hidedColumns}
        hidedSubColumns={hidedSubColumns}
        columns={columns}
        translate={environmentTranslation}
        translateForecast={t}
        onUpdateHidedColumns={onUpdateHidedColumns}
        onUpdateSubHidedColumns={onUpdateSubHidedColumns}
      />
    </Box>
  );
}

export default observer(ForecastTable);
